<template>
    <div class="container">
        <div class="title-container">
            <span>
                {{ $t('doe.titre') }}
                <ReglePastille cle="doe.definition" />
            </span>
        </div>
        <div class="content-container">
            <div class="dataTable">
                <div>
                    <h2>
                        {{ getTitreTableau }}
                        <ReglePastille cle="doe.portee" />
                    </h2>
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else ref="content" class="datatable-container" @scroll="handleScroll">
                    <DataTable
                        :isLoading="isLoading"
                        :noDataText="$t('search.notFound')"
                        :tableData="tableData"
                        :tableHeader="tableHeader"
                        @select="doSelect"
                        @sort="doSort"
                    />
                </div>
            </div>
        </div>
    </div>
    <WaitingModal v-if="isPdfLoading" />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import { TableHeader } from '@/models/TableHeader';
import { SortType, sortValue } from '@/models/SortType';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import { DocumentReglementaire } from '@/models/DocumentReglementaire';
import { DOE } from '@/modules/DOE/store/mutation-types';
import { DoeActions } from '@/modules/DOE/store/action-types';
import { DOEDTO } from '@/models/DOEDTO';
import dayjs from 'dayjs';
import i18n from '@/plugins/i18n';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'RapportPage',
    components: {
        DataTable,
        LoaderComponent,
        WaitingModal,
        ReglePastille,
    },
    beforeMount() {
        this.getDoeData();
    },
    mounted() {
        (this.$refs.content as HTMLElement)?.scrollTo({ top: this.scroll });
    },
    methods: {
        getDoeData(page = 0) {
            this.$store.dispatch(`doeState/${DoeActions.GET_DOE_PUBLIE}`, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
            });
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`doeState/${DOE.SET_SORT}`, { sortData, sortStatus });
            this.$store.commit(`doeState/${DOE.RESET_ITEMS}`);
            this.getDoeData();
        },
        async doSelect(item: { idMasque: number; libelle: string }) {
            await this.$store.dispatch(`doeState/${DoeActions.GET_PDF_DOE}`, item);
        },
        handleScroll() {
            const content = this.$refs.content as HTMLElement;
            const top = content?.scrollTop ?? 0;
            const max = content?.scrollHeight ?? 0;
            const height = content?.clientHeight ?? 0;
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.$store.commit(`doeState/${DOE.SET_PAGE}`, this.page + 1);
                this.getDoeData(this.page);
            }
        },
    },
    computed: {
        tableData(): DocumentReglementaire[] {
            if (this.$store.getters['doeState/getItems'] == null) {
                return [];
            }
            return this.$store.getters['doeState/getItems'].map(
                ({ id, libelleDoe, libelleChantier, libelleSite, dateMiseAJour }: DOEDTO) => {
                    return {
                        idMasque: id,
                        libelle: libelleDoe,
                        projet: libelleChantier,
                        site: libelleSite,
                        dateMiseAJour: dayjs(dateMiseAJour).format('DD/MM/YYYY'),
                        pdfExist: true,
                    };
                }
            );
        },
        tableHeader(): TableHeader[] {
            return this.$store.getters['doeState/getDoeTableHeader'];
        },
        isLoading() {
            return this.$store.getters['doeState/isLoading'];
        },
        isPdfLoading() {
            return this.$store.getters['doeState/isPdfLoading'];
        },
        page() {
            return this.$store.getters['doeState/getPage'];
        },
        numberOfPages() {
            return this.$store.getters['doeState/getNbOfPages'];
        },
        scroll() {
            return this.$store.getters['rapportState/getRapportScroll'];
        },
        count() {
            return this.$store.getters['doeState/getNbItems'];
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['doeState/getSort'];
        },
        getTitreTableau(): string {
            const param = { count: this.count };
            return i18n.global.t('doe.table.titre', param);
        },
    },
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';

.datatable-container {
    max-height: calc(100vh - 275px);
    overflow-y: auto;
}
@media screen and (max-width: 1631px) {
    .datatable-container {
        max-height: calc(100vh - 340px);
    }
}

@media screen and (max-width: 1536px) {
    .datatable-container {
        max-height: calc(100vh - 440px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 518px);
    }
}
@media screen and (max-width: 1536px) {
    .datatable-container[data-v-43297bee] {
        max-height: calc(100vh - 345px);
    }
}
:deep(.containerBtn) {
    height: 20px;
}
:deep(.icon) {
    height: 20px;
}
</style>
